// 증시 자금 동향 (2026-10-12) — 시장 지수 화면 아래
// 투자자예탁금 · 신용거래융자 잔고 · 코스피/코스닥 일별 거래대금 (금융투자협회 FreeSIS, 서버 GET /market-funds)
import { useState, useEffect, useMemo } from "react";
import { C, mono, BASE_URL } from "./utils";
import { Ico } from "./lib/icons";

const card = { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 14, padding: 18 };
const PERIODS = [["1m", "1개월", 31], ["3m", "3개월", 92], ["6m", "6개월", 183], ["1y", "1년", 366]];
const fmtJo = (v, d = 1) => v == null ? "—" : `${v.toFixed(d)}조`;
const sign = (v, d = 2) => v == null ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(d)}`;
const chgColor = (v) => v > 0 ? C.up : v < 0 ? C.down : "var(--text-sub)";

// 선·막대 그래프 — series: [{ key, name, color, type: 'line'|'bar', width }]
function Chart({ rows, series, height = 200, avgKey }) {
  const [hover, setHover] = useState(null);
  const W = 640, H = height, L = 44, R = 10, T = 10, B = 24;
  const vals = rows.flatMap(r => series.map(s => r[s.key])).filter(v => v != null);
  if (rows.length < 2 || !vals.length) return <div style={{ fontSize: 12.5, color: "var(--text-muted)", padding: "30px 0", textAlign: "center" }}>자료가 부족합니다.</div>;
  const hasBar = series.some(s => s.type === "bar");
  const lo = hasBar ? 0 : Math.min(...vals), hi = Math.max(...vals);
  const pad = (hi - lo) * 0.08 || hi * 0.05 || 1;
  const min = hasBar ? 0 : Math.max(0, lo - pad), max = hi + pad;
  const n = rows.length, step = (W - L - R) / n;
  const x = (i) => L + step * (i + 0.5);
  const y = (v) => T + (1 - (v - min) / (max - min || 1)) * (H - T - B);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => min + (max - min) * f);
  const lab = Math.max(1, Math.ceil(n / 6));
  const avg = avgKey ? rows.reduce((a, r) => a + (r[avgKey] || 0), 0) / rows.filter(r => r[avgKey] != null).length : null;
  const h = hover != null ? rows[hover] : null;
  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} onMouseLeave={() => setHover(null)}>
        {ticks.map((t, i) => <g key={i}><line x1={L} x2={W - R} y1={y(t)} y2={y(t)} stroke="var(--grid-line)" /><text x={L - 6} y={y(t) + 4} textAnchor="end" fontSize="10.5" fill="var(--text-muted)" style={mono}>{t >= 100 ? Math.round(t) : t.toFixed(t >= 10 ? 0 : 1)}</text></g>)}
        {rows.map((r, i) => (i % lab === 0 || i === n - 1) && <text key={r.date} x={x(i)} y={H - 6} textAnchor="middle" fontSize="10.5" fill="var(--text-muted)" style={mono}>{r.date.slice(5).replace("-", "/")}</text>)}
        {series.filter(s => s.type === "bar").map(s => rows.map((r, i) => r[s.key] == null ? null : <rect key={s.key + i} x={x(i) - Math.max(1, step * 0.36)} y={y(r[s.key])} width={Math.max(1.5, step * 0.72)} height={Math.max(0, y(min) - y(r[s.key]))} fill={s.color} opacity={hover === i ? 1 : 0.8} rx="1" />))}
        {avg != null && <><line x1={L} x2={W - R} y1={y(avg)} y2={y(avg)} stroke="var(--text-sub)" strokeDasharray="4 4" /><text x={W - R} y={y(avg) - 4} textAnchor="end" fontSize="10.5" fill="var(--text-sub)">기간 평균 {avg.toFixed(1)}조</text></>}
        {series.filter(s => s.type !== "bar").map(s => {
          const pts = rows.map((r, i) => r[s.key] == null ? null : `${x(i).toFixed(1)},${y(r[s.key]).toFixed(1)}`).filter(Boolean);
          return <polyline key={s.key} points={pts.join(" ")} fill="none" stroke={s.color} strokeWidth={s.width || 2.2} strokeLinejoin="round" />;
        })}
        {rows.map((r, i) => <rect key={"h" + i} x={x(i) - step / 2} y={T} width={step} height={H - T - B} fill="transparent" onMouseEnter={() => setHover(i)} />)}
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} stroke="var(--text-muted)" strokeDasharray="3 3" />}
      </svg>
      {h && (
        <div style={{ position: "absolute", top: 2, left: `${Math.min(70, Math.max(4, (x(hover) / W) * 100 - 12))}%`, background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 10px", fontSize: 12, color: "var(--text)", pointerEvents: "none", boxShadow: "0 4px 14px rgba(0,0,0,0.18)", whiteSpace: "nowrap" }}>
          <div style={{ color: "var(--text-muted)", fontSize: 11, marginBottom: 2 }}>{h.date}</div>
          {series.map(s => h[s.key] != null && <div key={s.key}><span style={{ color: s.color }}>●</span> {s.name} <b style={mono}>{fmtJo(h[s.key], 2)}</b></div>)}
        </div>
      )}
    </div>
  );
}

function Panel({ title, sub, rows, all, series, keyMain, avgKey, legend }) {
  const withVal = all.filter(r => r[keyMain] != null);
  const last = withVal[withVal.length - 1], prev = withVal[withVal.length - 2];
  const first = rows.find(r => r[keyMain] != null);
  const d1 = last && prev ? last[keyMain] - prev[keyMain] : null;
  // 거래대금처럼 날마다 출렁이는 값은 '기간 평균 대비', 잔고(예탁금·신용융자)는 '기간 첫날 대비'
  const base = avgKey ? rows.filter(r => r[keyMain] != null).reduce((a, r, _, arr) => a + r[keyMain] / arr.length, 0) : first?.[keyMain];
  const dp = last && base ? (last[keyMain] / base - 1) * 100 : null;
  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>{title} <span style={{ fontSize: 11.5, fontWeight: 400, color: "var(--text-muted)" }}>{sub}</span></div>
        <span style={{ ...mono, fontSize: 11, color: "var(--text-muted)" }}>{last?.date}</span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, margin: "6px 0 8px", flexWrap: "wrap" }}>
        <span style={{ ...mono, fontSize: 24, fontWeight: 700, color: "var(--text)" }}>{fmtJo(last?.[keyMain], 2)}</span>
        <span style={{ ...mono, fontSize: 12.5, color: chgColor(d1) }}>전일 {sign(d1)}조</span>
        <span style={{ ...mono, fontSize: 12.5, color: chgColor(dp) }}>{avgKey ? "기간 평균 대비" : "기간"} {dp == null ? "—" : `${dp > 0 ? "+" : ""}${dp.toFixed(1)}%`}</span>
      </div>
      {legend && <div style={{ display: "flex", gap: 12, fontSize: 11.5, color: "var(--text-sub)", marginBottom: 4 }}>{series.map(s => <span key={s.key}><span style={{ color: s.color }}>●</span> {s.name}{last?.[s.key] != null ? ` ${fmtJo(last[s.key], 1)}` : ""}</span>)}</div>}
      <Chart rows={rows} series={series} avgKey={avgKey} />
    </div>
  );
}

export default function MarketFunds() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [p, setP] = useState("3m");
  useEffect(() => {
    let alive = true;
    fetch(`${BASE_URL}/market-funds`).then(r => r.json()).then(j => { if (!alive) return; if (j.rows?.length) setD(j); else setErr(j.error || "자료 없음"); }).catch(e => alive && setErr(e.message));
    return () => { alive = false; };
  }, []);
  const rows = useMemo(() => {
    if (!d) return [];
    const days = PERIODS.find(x => x[0] === p)[2];
    const lastDate = new Date(d.rows[d.rows.length - 1].date);
    const cut = new Date(lastDate.getTime() - days * 86400000).toISOString().slice(0, 10);
    return d.rows.filter(r => r.date > cut);
  }, [d, p]);
  const btn = (on) => ({ padding: "5px 12px", borderRadius: 8, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", fontWeight: on ? 700 : 500, border: "1px solid " + (on ? "var(--primary)" : "var(--border)"), background: on ? "var(--sb-active-bg)" : "transparent", color: on ? "var(--sb-active-text)" : "var(--text-sub)" });
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center" }}><Ico n="coins" />증시 자금 동향</div>
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>투자자예탁금 · 신용융자 · 거래대금 (일별)</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>{PERIODS.map(([k, t]) => <button key={k} onClick={() => setP(k)} style={btn(p === k)}>{t}</button>)}</div>
      </div>
      {!d && <div style={{ ...card, fontSize: 13, color: err ? "var(--text-sub)" : "var(--text-muted)" }}>{err ? `증시 자금 자료를 가져오지 못했습니다 (${err}).` : "불러오는 중…"}</div>}
      {d && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 12 }}>
            <Panel title="투자자예탁금" sub="증시 대기 자금" rows={rows} all={d.rows} keyMain="deposit" series={[{ key: "deposit", name: "투자자예탁금", color: "#3b82f6" }]} />
            <Panel title="신용거래융자 잔고" sub="빚내서 산 주식" rows={rows} all={d.rows} keyMain="credit" legend
              series={[{ key: "credit", name: "전체", color: "#f97316", width: 2.6 }, { key: "creditKospi", name: "코스피", color: "#8b5cf6", width: 1.6 }, { key: "creditKosdaq", name: "코스닥", color: "#14b8a6", width: 1.6 }]} />
            <Panel title="코스피 거래대금" sub="하루 총액" rows={rows} all={d.rows} keyMain="kospiValue" avgKey="kospiValue" series={[{ key: "kospiValue", name: "코스피", color: "#6366f1" }]} />
            <Panel title="코스닥 거래대금" sub="하루 총액" rows={rows} all={d.rows} keyMain="kosdaqValue" avgKey="kosdaqValue" series={[{ key: "kosdaqValue", name: "코스닥", color: "#10b981" }]} />
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 8, lineHeight: 1.7 }}>
            출처: <a href={d.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--link)" }}>{d.source}</a> · 단위 조원 · 금융투자협회가 전 영업일 자료를 다음 영업일 오후에 올려서 하루 늦게 반영되며, 이 화면은 매일 19:00(한국 시간)에 한 번 새로 읽습니다. · 투자자예탁금은 주식을 사려고 증권 계좌에 넣어 둔 돈, 신용거래융자는 증권사에서 돈을 빌려 산 주식 금액입니다.
          </div>
        </>
      )}
    </div>
  );
}
